/**
 * "svc-promo has already DISPATCHED this claim's bonus to the wallet."
 *
 * `promotion_claims.credit_attempted_at` is stamped BEFORE the wallet call and
 * deliberately survives the rollback that follows a lost reply, so it does not
 * mean "the credit succeeded" — it means the money may be in the player's pool
 * with nothing on this screen able to tell. Every backoffice surface that
 * renders the stamp or gates a rejection on it reads THIS module, so the marker
 * on the row and the confirmation on the button can never disagree about which
 * rows are hazardous.
 *
 * The value arrives as an ISO string over the wire; older svc-promo builds and
 * the never-dispatched rows both send null.
 */
export interface CreditAttemptRow {
  status?: string;
  creditAttemptedAt?: string | null;
}

export function isCreditAttempted(row: CreditAttemptRow): boolean {
  return typeof row.creditAttemptedAt === 'string' && row.creditAttemptedAt.trim() !== '';
}

/**
 * The stamp ALONE is not a hazard, and styling it as one destroys its own
 * signal: `approveClaim` stamps every approval that moved money, so ordinary
 * approved / wagering / completed rows all carry it. An operator shown the same
 * alarm on fifty healthy rows learns the colour means nothing, and the one row
 * that matters disappears into them.
 *
 * A row is hazardous only when it is stamped AND still pending: the approval
 * dispatched the money and then failed, the row went back to the queue, and
 * Reject on it releases a turnover lock on money the player may already hold —
 * permanently, since 'rejected' falls outside the live-claim predicate and
 * re-approving cannot bring the lock back.
 */
export function isRejectHazard(row: CreditAttemptRow): boolean {
  return row.status === 'pending' && isCreditAttempted(row);
}
